import en from "@/locales/en.json";
import tr from "@/locales/tr.json";

export const locales = ["tr", "en"] as const;
export type Locale = (typeof locales)[number];

const dictionaries = { tr, en } as const;

export type Dictionary = typeof tr;

export function isLocale(value: string): value is Locale {
  return locales.includes(value as Locale);
}

export function getDictionary(locale: Locale): Dictionary {
  return dictionaries[locale];
}

export function formatCopyright(template: string, year: number): string {
  return template.replace("{year}", String(year));
}

export type SectionKey = keyof Dictionary["sections"];

/** URL path segment per locale (ör. /hakkimizda, /en/about) */
const SECTION_PATHS: Record<Locale, Record<SectionKey, string>> = {
  tr: {
    about: "hakkimizda",
    features: "moduller",
    references: "referanslar",
    team: "ekip",
    contact: "iletisim",
  },
  en: {
    about: "about",
    features: "modules",
    references: "references",
    team: "team",
    contact: "contact",
  },
};

export function localePath(locale: Locale): string {
  return locale === "en" ? "/en" : "/";
}

/** Fiyatlandırma sayfası (SectionKey dışı sabit çift): /fiyatlandirma ↔ /en/pricing */
export function pricingPath(locale: Locale): string {
  return locale === "en" ? "/en/pricing" : "/fiyatlandirma";
}

/** Kurumsal kimlik (marka kiti) sayfası: /kurumsal-kimlik ↔ /en/brand */
export function brandPath(locale: Locale): string {
  return locale === "en" ? "/en/brand" : "/kurumsal-kimlik";
}

/** Tam sayfa URL’si: /hakkimizda veya /en/about */
export function sectionPagePath(locale: Locale, key: SectionKey): string {
  const base = localePath(locale);
  const seg = SECTION_PATHS[locale][key];
  if (base === "/") return `/${seg}`;
  return `${base}/${seg}`;
}

/** Eski hash tabanlı API; artık tam sayfa yoluna yönlendirir */
export function sectionHref(locale: Locale, key: SectionKey): string {
  return sectionPagePath(locale, key);
}

export function sectionMetaTitle(locale: Locale, key: SectionKey): string {
  const d = getDictionary(locale);
  const t = {
    about: d.about.title,
    features: d.features.title,
    references: d.references.title,
    team: d.team.title,
    contact: d.contact.title,
  }[key];
  return `${t} — ${d.brand.name}`;
}

/** SEO `title.template` için yalnızca sayfa başlığı (marka `| oravity` ile eklenir) */
export function sectionSeoTitle(locale: Locale, key: SectionKey): string {
  const d = getDictionary(locale);
  return {
    about: d.about.title,
    features: d.features.title,
    references: d.references.title,
    team: d.team.title,
    contact: d.contact.title,
  }[key];
}

/** Bölüm sayfası `meta description` (site geneli `meta.description` ile karıştırılmaz). */
export function sectionMetaDescription(locale: Locale, key: SectionKey): string {
  const d = getDictionary(locale);
  return d.sectionMeta[key];
}

/**
 * hreflang alternatifleri: `/hakkimizda` ↔ `/en/about`, `/` ↔ `/en`
 */
export function getAlternateLanguagePaths(pathname: string): { tr: string; en: string } {
  const raw = pathname.replace(/\/$/, "") || "/";

  if (raw === "/" || raw === "/en") {
    return { tr: "/", en: "/en" };
  }

  // SectionKey dışı sabit sayfa çiftleri
  if (raw === "/sss" || raw === "/en/faq") {
    return { tr: "/sss", en: "/en/faq" };
  }
  if (raw === "/fiyatlandirma" || raw === "/en/pricing") {
    return { tr: "/fiyatlandirma", en: "/en/pricing" };
  }
  if (raw === "/kurumsal-kimlik" || raw === "/en/brand") {
    return { tr: "/kurumsal-kimlik", en: "/en/brand" };
  }

  if (raw.startsWith("/en/")) {
    const seg = raw.slice(4);
    const hit = (Object.entries(SECTION_PATHS.en) as [SectionKey, string][]).find(
      ([, s]) => s === seg,
    );
    if (hit) {
      return { tr: sectionPagePath("tr", hit[0]), en: raw };
    }
  } else if (raw.startsWith("/")) {
    const seg = raw.slice(1);
    const hit = (Object.entries(SECTION_PATHS.tr) as [SectionKey, string][]).find(
      ([, s]) => s === seg,
    );
    if (hit) {
      return { tr: raw, en: sectionPagePath("en", hit[0]) };
    }
  }

  return { tr: "/", en: "/en" };
}

/** Örn. /hakkimizda ↔ /en/about — dil anahtarı için hedef URL */
export function alternateLocaleHref(pathname: string, currentLocale: Locale): string {
  const path = pathname.replace(/\/$/, "") || "/";

  if (currentLocale === "tr") {
    if (path === "/") return "/en";
    if (path === "/sss") return "/en/faq";
    if (path === "/fiyatlandirma") return "/en/pricing";
    if (path === "/kurumsal-kimlik") return "/en/brand";
    const hit = (Object.entries(SECTION_PATHS.tr) as [SectionKey, string][]).find(
      ([, seg]) => path === `/${seg}`,
    );
    return hit ? sectionPagePath("en", hit[0]) : "/en";
  }

  if (path === "/en") return "/";
  if (path === "/en/faq") return "/sss";
  if (path === "/en/pricing") return "/fiyatlandirma";
  if (path === "/en/brand") return "/kurumsal-kimlik";
  const hit = (Object.entries(SECTION_PATHS.en) as [SectionKey, string][]).find(
    ([, seg]) => path === `/en/${seg}`,
  );
  return hit ? sectionPagePath("tr", hit[0]) : "/";
}
